import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material';
import { enUS, huHU, esES } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import huLocale from 'date-fns/locale/hu';
import enLocale from 'date-fns/locale/en-GB';
import esLocale from 'date-fns/locale/es';
import TextField from '@mui/material/TextField';
import Utils from '../../../lib/utils';

function getLocales(language = 'en'): [typeof huHU, Locale, string, string] {
	switch (language) {
		// case 'en':
		// 	return [enUS, enLocale, 'dd.MM.yyyy', 'DD.MM.YYYY'];
		// case 'hu':
		// default:
		// 	return [huHU, huLocale, 'yyyy.MM.dd', 'ÉÉÉÉ.HH.NN'];
		case 'hu':
			return [huHU, huLocale, 'yyyy.MM.dd', 'ÉÉÉÉ.HH.NN'];
		case 'es':
			return [esES, esLocale, 'dd/MM/yyyy', 'DD/MM/AAAA'];
		case 'en':
		default:
			return [enUS, enLocale, 'dd/MM/yyyy', 'DD/MM/YYYY'];
	}
}

function CustomDatePicker(props: ComponentProps, ref: any) {
	const [date, setDate] = useState(getDateValue(props.value));
	const [key, setKey] = useState(0);
	const [language] = useState(props.language || 'hu');

	const [datePickerLocale, dateFNSLocale, dateFormat, datePlaceholder] = useMemo(
		() => getLocales(language),
		[language]
	);

	const theme = createTheme(
		{
			components: {
				MuiInputBase: {
					styleOverrides: {
						root: {
							height: 34,
							marginTop: 5,
							border: 'none',
							fontSize: '16px',
						},
					},
				},
				MuiFormControl: {
					styleOverrides: {
						root: {
							border: 'none',
							width: '100%',
						},
					},
				},
				MuiOutlinedInput: {
					styleOverrides: {
						notchedOutline: {
							// border: 'none',
							legend: {
								display: 'none',
							},
						},
					},
				},
			},
		},
		datePickerLocale
	);

	function onDateChange(date: any, keyboardInputValue?: string) {
		// Ha a user nem két számjeggyel írja be a napot, hanem csak egyel, pl. 2022.12.1, akkor az onchange esemény rögtön beugrana, és beállítaná 2022.12.01-re az értéket, ezáltal nem tudna rendesen dátumot beírni. Ezért csak akkor triggereljük az onChange eventet, ha a beírt érték a rendesen formázott dátum. Viszont ha a user nem szeretne 2 számjegyű napokat használni, azaz 2022.12.1-et ír be, és elkattint az inputból, akkor az onBlur esemény ugrik be. Ez megnézi az input value értékét, és azt állítja be 2022.12.1 -> 2022.12.01.
		if (keyboardInputValue && keyboardInputValue.length !== 10) {
			return;
		}
		setDate(date);
		props.onChange && props.onChange(date);
	}

	function onBlur(event: any) {
		//Mobil nézetnél csak a dialogus működik, viszont ennél bezavar az onBlur esemény, ezért ezt csak desktopon használjuk
		if (!Utils.isMobile() && language !== 'en') {
			// Safari nem fogadja el a YYYY.MM.DD formátumot ezért át kell alakítani YYYY/MM/DD-re
			const value = event.target.value.replace(/\./g, '/');
			const _date = new Date(value);

			if (!date || _date.getTime() !== date.getTime()) {
				setDate((prevValue) => _date);
				// Fontos a key növelés, különben nem formázná ba ugyanazt a napot
				setKey((prevValue) => prevValue + 1);
				props.onChange && props.onChange(_date);
			}
		}
	}

	function getValue() {
		return date;
	}

	function setValue(value: inputDate) {
		setDate(getDateValue(value));
	}

	useImperativeHandle(
		ref,
		() => ({
			getValue,
			setValue,
		}),
		[date]
	);

	useEffect(() => {
		setDate(getDateValue(props.value));
	}, [props.value]);
	return (
		<ThemeProvider theme={theme}>
			<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFNSLocale}>
				<DatePicker
					disableFuture={props.disableFuture || false}
					disablePast={props.disablePast || false}
					inputFormat={dateFormat}
					views={['year', 'month', 'day']}
					value={date}
					disabled={props.disabled ? true : false}
					disableOpenPicker={props.disableOpenPicker || false}
					onChange={(date: any, keyboardInputValue: string | undefined) =>
						onDateChange(date, keyboardInputValue)
					}
					key={key}
					renderInput={(params) => (
						<TextField
							{...params}
							onBlur={onBlur}
							inputProps={{
								...params.inputProps,
								placeholder: props.placeholder || datePlaceholder,
							}}
							error={props.required && (!date || !Utils.isValidDate(date))}
						/>
					)}
				/>
			</LocalizationProvider>
		</ThemeProvider>
	);
}

function getDateValue(date: inputDate) {
	if (typeof date === 'string') {
		return new Date(date.replace(' ', 'T'));
	} else {
		return date;
	}
}
type inputDate = string | Date | null | undefined;

interface ComponentProps {
	value?: string | Date | null;
	disableFuture?: boolean;
	disablePast?: boolean;
	disableOpenPicker?: boolean;
	onChange?: Function;
	required?: boolean;
	disabled?: boolean;
	placeholder?: string;
	language?: 'hu' | 'en' | 'es';
}
export default forwardRef(CustomDatePicker);
